// Избранное (ручки /account/favorites/). Вид спорта сервер берёт из токена ПРИЛОЖЕНИЯ: entity_id в теле — голое число.
// - Вошёл через Telegram: набор — авторитетный ответ сервера (вход несёт `favorites`; POST/DELETE отвечают полным
//   набором) — рисуем ЕГО, не оптимистичную догадку. Ответ входа без ключа `favorites` (старая сборка сервера) —
//   набор отдельным GET.
// - Гость (вход не удался или открыто вне Telegram): набор хранится локально (`football.favorites.local`). После
//   успешного входа (И-3.3 AC-6) — ОДИН POST account/favorites/merge/ {local_favorites}: сервер вливает их в аккаунт и
//   отвечает полным набором, локальные очищаются. Уходит только то, чего в аккаунте нет, и не больше свободного места
//   под потолком (явная ручка слияния отвечает на превышение 400 на весь пакет). Сбой сети/5xx/сессии — локальные
//   остаются (звёзды горят: пока слияние не прошло, вошедшему виден аккаунт ∪ локальные) и уходят при следующей
//   синхронизации: новая сессия, возврат сети, открытие вкладки Избранного, следующее открытие. Отказ 4xx (пакет
//   отвергнут) — не повторяем: локальные очищаются, набор перечитывается с сервера.
// - Лимиты (HQ-269): потолки config.favorite_limits (нет в конфиге — 30 лига/команда/игрок, 100 матчей). Добавление
//   сверх потолка не уходит ни на сервер, ни в локальный набор гостя — FavoriteLimitError (звезда показывает уведомление,
//   как iOS). Снимать можно всегда, даже если набор после слияния гостя больше потолка (гид v5 §избранное). Отказ
//   сервера favorite_limit_exceeded — та же ошибка с его числом. Одна точка для всех звёзд (строка матча, лига, табло,
//   вкладка Избранного).
import { useMutation } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { ApiError, apiRequest } from '../api/client';
import { CONFIG_KEY, type AppConfig } from '../api/config';
import { queryClient } from '../api/queryClient';
import { hasSession, onSessionChange, setFavoritesBridge, type FavoriteRow, type TokenResponse } from './session';

export type FavType = 'team' | 'league' | 'player' | 'match';
export const FAV_TYPES: FavType[] = ['team', 'league', 'player', 'match'];
export type FavoritesSet = Record<FavType, number[]>;
export const EMPTY: FavoritesSet = { team: [], league: [], player: [], match: [] };

const PATH = '/api/v5/mobile/account/favorites/';
const MERGE_PATH = `${PATH}merge/`;
const LOCAL_KEY = 'football.favorites.local';
type FavoritesResponse = Partial<Record<string, FavoriteRow[]>>;

/** Ответ сервера (сгруппирован по типам; лишние ключи вроде country игнорируем) → наш набор. */
export function toSet(response: Partial<Record<string, FavoriteRow[]>> | null | undefined): FavoritesSet {
  const out: FavoritesSet = { team: [], league: [], player: [], match: [] };
  for (const type of FAV_TYPES) out[type] = (response?.[type] ?? []).map((r) => Number(r.entity_id)).filter(Number.isFinite);
  return out;
}

function loadLocal(): FavoritesSet {
  try {
    const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) ?? 'null') as Partial<FavoritesSet> | null;
    const out: FavoritesSet = { team: [], league: [], player: [], match: [] };
    for (const type of FAV_TYPES) out[type] = (raw?.[type] ?? []).map(Number).filter(Number.isFinite);
    return out;
  } catch { return { ...EMPTY }; }
}
function saveLocal(set: FavoritesSet): void {
  try {
    if (isEmptySet(set)) localStorage.removeItem(LOCAL_KEY); else localStorage.setItem(LOCAL_KEY, JSON.stringify(set));
  } catch { /* приватный режим */ }
}
const isEmptySet = (set: FavoritesSet) => FAV_TYPES.every((type) => set[type].length === 0);

let local: FavoritesSet = loadLocal();
/** Набор аккаунта; null — ещё не знаем (до входа или ответ без ключа). */
let account: FavoritesSet | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function setLocal(next: FavoritesSet): void { local = next; saveLocal(next); }

/** Аккаунт ∪ ещё не слитые локальные (id аккаунта первыми). Тот же объект, пока части не менялись (useSyncExternalStore). */
let unionCache: { account: FavoritesSet | null; local: FavoritesSet; value: FavoritesSet } | null = null;
function accountView(): FavoritesSet {
  const base = account ?? EMPTY;
  if (isEmptySet(local)) return base;
  if (unionCache?.account === account && unionCache.local === local) return unionCache.value;
  const value = { ...EMPTY };
  for (const type of FAV_TYPES) value[type] = [...new Set([...base[type], ...local[type]])];
  unionCache = { account, local, value };
  return value;
}

/** Текущий набор: вошедшему — аккаунта (с ещё не слитыми локальными), гостю — локальный. */
export function currentFavorites(): FavoritesSet {
  return hasSession() ? accountView() : local;
}

/** Набор известен: гостю — всегда (локальный), вошедшему — когда сервер его прислал. */
export function favoritesKnown(): boolean {
  return !hasSession() || account !== null;
}

/** Не слитое с аккаунтом локальное избранное гостя (для экрана и тестов). */
export function pendingLocalFavorites(): FavoritesSet { return local; }

setFavoritesBridge({
  accepted(favorites: TokenResponse['favorites']) {
    account = favorites ? toSet(favorites) : null;
    emit();
  },
});
// новая сессия (вход, обновление) — синхронизировать: набор аккаунта, если его нет, и слияние гостя
onSessionChange(() => {
  if (!hasSession()) account = null;
  emit();
  if (hasSession()) void syncFavorites();
});
if (typeof window !== 'undefined') window.addEventListener('online', () => { void syncFavorites(); });

async function fetchAccount(): Promise<void> {
  try {
    account = toSet(await apiRequest<FavoritesResponse>('GET', PATH, { auth: true }));
    emit();
  } catch { /* звёзды погашены до следующего действия; ошибка не мешает экрану */ }
}

/** Пакет слияния: локальные id, которых нет в аккаунте, — не больше свободного места под потолком типа. Набор аккаунта
 *  не известен (старый сервер, GET не удался) — все локальные: дубли сервер пропустит сам. */
export function mergeItems(pending: FavoritesSet, into: FavoritesSet | null, limits?: Record<string, number> | null): Array<{ entity_type: FavType; entity_id: number }> {
  return FAV_TYPES.flatMap((type) => {
    const fresh = [...new Set(pending[type])].filter((id) => !into?.[type].includes(id));
    const room = into ? Math.max(0, favoriteLimit(type, limits) - into[type].length) : fresh.length;
    return fresh.slice(0, room).map((id) => ({ entity_type: type, entity_id: id }));
  });
}

/** Пакет отвергнут по существу (4xx, кроме сессии и 429): повтор бессмысленен (гид v5 §2.6). */
const isRejectedPackage = (e: unknown) =>
  e instanceof ApiError && e.kind === 'http' && e.status >= 400 && e.status < 500 && ![401, 403, 408, 429].includes(e.status);

async function mergeLocal(): Promise<void> {
  if (isEmptySet(local)) return;
  const sent = local;
  const items = mergeItems(sent, account, configLimits());
  const forget = () => {
    // за время запроса гость не менялся (вошёл), но звезду могли снять у ещё не слитого id — его уже нет в local
    const next = { ...EMPTY };
    for (const type of FAV_TYPES) next[type] = local[type].filter((id) => !sent[type].includes(id));
    setLocal(next);
  };
  if (!items.length) { forget(); emit(); return; }
  try {
    const response = await apiRequest<FavoritesResponse>('POST', MERGE_PATH, { auth: true, body: { local_favorites: items } });
    account = toSet(response);
    forget();
    emit();
  } catch (e) {
    if (!isRejectedPackage(e)) return; // сеть, 5xx, сессия — локальные ждут следующей синхронизации
    forget();
    emit();
    await fetchAccount();
  }
}

let syncing: Promise<void> | null = null;
/** Синхронизация вошедшего (single-flight): набора аккаунта нет — спросить; есть локальные гостя — слить. Гостю — ничего.
 *  Зовут: вход/обновление сессии, main.tsx после входа, вкладка Избранного, возврат сети. */
export function syncFavorites(): Promise<void> {
  if (!hasSession()) return Promise.resolve();
  if (account !== null && isEmptySet(local)) return syncing ?? Promise.resolve();
  if (!syncing) {
    syncing = (async () => {
      if (account === null) await fetchAccount();
      if (hasSession()) await mergeLocal();
    })().finally(() => { syncing = null; });
  }
  return syncing;
}

const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };

export function useHasSession(): boolean {
  return useSyncExternalStore(onSessionChange, hasSession, hasSession);
}

export function useFavorites(): FavoritesSet {
  return useSyncExternalStore(subscribe, currentFavorites, currentFavorites);
}

export function useFavoritesKnown(): boolean {
  return useSyncExternalStore(subscribe, favoritesKnown, favoritesKnown);
}

export class FavoriteLimitError extends Error {
  constructor(readonly type: FavType, readonly limit: number) { super('favorite_limit_exceeded'); }
}

/** Потолки по умолчанию (MLS-Backend apps/accounts/constants.py) — если конфиг их не прислал. */
export const DEFAULT_FAVORITE_LIMITS: Record<FavType, number> = { league: 30, team: 30, player: 30, match: 100 };

/** Потолок типа: из config.favorite_limits, иначе по умолчанию. */
export function favoriteLimit(type: FavType, limits?: Record<string, number> | null): number {
  const value = limits?.[type];
  return typeof value === 'number' && value > 0 ? value : DEFAULT_FAVORITE_LIMITS[type];
}

/** Добавить id нельзя: в наборе уже потолок (или больше). Уже отмеченный id лимит не трогает. */
export function isLimitReached(set: FavoritesSet, type: FavType, id: number, limits?: Record<string, number> | null): boolean {
  return !set[type].includes(id) && set[type].length >= favoriteLimit(type, limits);
}

const configLimits = () => queryClient.getQueryData<AppConfig>(CONFIG_KEY)?.favorite_limits;

/** Отметить / снять. on — сейчас отмечено (значит, снимаем). */
export async function toggleFavorite({ type, id, on }: { type: FavType; id: number; on: boolean }): Promise<FavoritesSet> {
  if (!on && isLimitReached(currentFavorites(), type, id, configLimits())) {
    throw new FavoriteLimitError(type, favoriteLimit(type, configLimits()));
  }
  if (!hasSession()) {
    const ids = local[type].filter((x) => x !== id);
    setLocal({ ...local, [type]: on ? ids : [...ids, id] });
    emit();
    return local;
  }
  // вошедший снимает id, который ещё не слит из гостевого набора, — убрать и оттуда (DELETE сервер примет и без строки)
  if (on && local[type].includes(id)) { setLocal({ ...local, [type]: local[type].filter((x) => x !== id) }); emit(); }
  try {
    const response = on
      ? await apiRequest<Partial<Record<string, FavoriteRow[]>>>('DELETE', `${PATH}${type}/${id}/`, { auth: true })
      : await apiRequest<Partial<Record<string, FavoriteRow[]>>>('POST', PATH, { auth: true, body: { entity_type: type, entity_id: id } });
    account = toSet(response);
    emit();
    return currentFavorites();
  } catch (e) {
    if (e instanceof ApiError && e.code === 'favorite_limit_exceeded') {
      throw new FavoriteLimitError(type, Number(e.payload?.limit) || favoriteLimit(type, configLimits()));
    }
    throw e;
  }
}

export function useToggleFavorite() {
  return useMutation({ mutationFn: toggleFavorite });
}

/** Параметры набора для полки и хаба: все четыре ключа ВСЕГДА (пустой перекрывает серверный), id без дублей по порядку. */
export function favoriteParams(set: FavoritesSet): Record<'teams' | 'leagues' | 'players' | 'matches', string> {
  const join = (ids: number[]) => [...new Set(ids)].sort((a, b) => a - b).join(',');
  return { teams: join(set.team), leagues: join(set.league), players: join(set.player), matches: join(set.match) };
}
